import React, { useState, useCallback } from 'react';
import { useClients } from '../hooks/useClients';
import { usePermissions } from '../hooks/usePermissions';
import { formatRut } from '../utils/rutValidator';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../components/ui/table';
import { Plus, Search } from 'lucide-react';
import ClientForm from '../components/clients/ClientForm';
import type { Client } from '../types/client';

const Clients: React.FC = () => {
  const { canManageClients } = usePermissions();

  // Search & filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  // Form dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // Debounce search input
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value.trim());
    }, 300);
  }, []);

  // Fetch clients with server-side search
  const { data: clientsData, isLoading, error } = useClients({
    search: debouncedSearch || undefined,
    active: includeInactive ? undefined : true,
  });

  const clients = clientsData?.items || [];

  // Handle creating a new client
  const handleCreate = () => {
    setSelectedClient(null);
    setFormOpen(true);
  };

  // Handle clicking a row
  const handleRowClick = (client: Client) => {
    setSelectedClient(client);
    setFormOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error al cargar clientes: {String(error)}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-3xl font-bold text-gray-900">Gestión Clientes</h1>
        {canManageClients && (
          <Button onClick={handleCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            Crear Cliente
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Buscar por RUT, nombre o nombre fantasía..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="pl-10"
          />
        </div>

        {/* Include inactive toggle */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="includeInactive"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="includeInactive" className="text-sm text-gray-700 cursor-pointer">
            Incluir clientes inactivos
          </label>
        </div>
      </div>

      {/* Clients table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {clients.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {debouncedSearch
              ? 'No se encontraron clientes que coincidan con la búsqueda'
              : 'No hay clientes registrados'}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>RUT</TableHead>
                <TableHead>Razón Social</TableHead>
                <TableHead>Nombre Fantasía</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((client) => (
                <TableRow
                  key={client.clientId}
                  onClick={() => handleRowClick(client)}
                  className="cursor-pointer"
                >
                  <TableCell className="font-mono text-sm">
                    {formatRut(client.rut)}
                  </TableCell>
                  <TableCell className="font-medium">{client.name}</TableCell>
                  <TableCell className="text-gray-600">
                    {client.fantasyName || '—'}
                  </TableCell>
                  <TableCell className="text-gray-600">
                    {client.email || '—'}
                  </TableCell>
                  <TableCell>
                    {client.active ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Activo
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Inactivo
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Client Form Dialog */}
      <ClientForm
        open={formOpen}
        onOpenChange={setFormOpen}
        client={selectedClient}
        readOnly={!canManageClients}
      />
    </div>
  );
};

export default Clients;
